import React, { useEffect, useMemo, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../base/hooks';
import { vacancyFormActions } from '../../redux/slices/vacancyFormSlice';
import { __t } from '../../../../../base/utils/t';
import { getRefById, RefMap, scrollToError } from '../../../../../base/utils';

import { Icon } from '../../../../../base/components/Icon';
import { HelperText } from '../../../../../base/components/HelperText';
import { Select, SelectOption } from '../../../../../base/components/Forms/Select';
import { Range } from '../../../../../base/components/Forms/Range';
import { ErrorMessage } from '../../../../../base/components/Forms/ErrorMessage';
import { Rate } from '../../../../../base/enums';

import './styles.scss';

export const Step3 = () => {
    const dispatch = useAppDispatch();
    const {
        fields: { rateType, rateFrom, rateTo, hoursPerMonthFrom, hoursPerMonthTo, currencyId },
        errors,
        shouldScrollToError,
    } = useAppSelector((state) => state.vacancyForm);
    const { currency } = useAppSelector((state) => state.employerApp.config);

    const refMap: RefMap[] = [
        {
            id: 'rateFrom',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'hoursPerMonthFrom',
            ref: useRef<HTMLDivElement>(null),
        },
    ];

    useEffect(() => {
        if (errors && shouldScrollToError) {
            scrollToError(errors, refMap);
            dispatch(vacancyFormActions.setShouldScrollToError(false));
        }
    }, [refMap, errors, shouldScrollToError]);

    const currencyOptions: SelectOption[] = useMemo(
        () =>
            currency.map((c) => ({
                value: c.id as unknown as string,
                text: c.name,
                additionalText: `${c.code} - ${c.sign}`,
            })),
        [currency],
    );

    const inputChangeHandler = (id: string, value: unknown) => {
        dispatch(vacancyFormActions.setField({ id, value }));
    };

    return (
        <div className='vacancy-form vacancy-form-step-3'>
            <div className='vacancy-form-step-3__wrapper'>
                <HelperText className='vacancy-form-step-3__helper-text' icon='arrow-down'>
                    <p>
                        {__t('Нажмите')}&nbsp;
                        <span>{__t('Месяц')}</span>&nbsp;
                        {__t('чтобы переключить период ставки.')}
                    </p>
                </HelperText>

                <div
                    className='vacancy-form__item scroll-margin-for-fixed-header'
                    ref={getRefById('rateFrom', refMap)?.ref}
                >
                    <div className='vacancy-form__label vacancy-form-step-3__label-with-select'>
                        {__t('Ставка, нетто в:')}
                        <div className='vacancy-form-step-3__label-wrap'>
                            <span
                                onClick={() => inputChangeHandler('rateType', Rate.Hourly)}
                                className={`vacancy-form-step-3__label-select ${
                                    rateType === Rate.Hourly ? 'vacancy-form-step-3__label-select--active' : ''
                                }`}
                            >
                                <Icon
                                    icon='clock-regular'
                                    color={rateType === Rate.Hourly ? 'default' : 'info'}
                                    className='vacancy-form-step-3__label-select-icon'
                                />
                                {__t('Час')}
                            </span>
                            <span
                                className={`vacancy-form-step-3__label-select ${
                                    rateType === Rate.Monthly ? 'vacancy-form-step-3__label-select--active' : ''
                                }`}
                                onClick={() => inputChangeHandler('rateType', Rate.Monthly)}
                            >
                                <Icon
                                    icon='calendar-regular'
                                    color={rateType === Rate.Monthly ? 'default' : 'info'}
                                    className='vacancy-form-step-3__label-select-icon'
                                />
                                {__t('Месяц')}
                            </span>
                        </div>
                    </div>
                    <Range
                        idFrom='rateFrom'
                        idTo='rateTo'
                        fromValue={rateFrom}
                        toValue={rateTo}
                        onChange={inputChangeHandler}
                        minValue='1'
                        removeBorderRadius='bottom'
                    />
                    <Select
                        id='currencyId'
                        value={currencyId as unknown as string}
                        options={currencyOptions}
                        removeBorderRadius='top'
                        onChange={inputChangeHandler}
                        className='vacancy-form-step-3__currency-select'
                    />
                    <ErrorMessage errors={errors?.rateFrom} />
                    <ErrorMessage errors={errors?.rateTo} />
                </div>

                <div
                    className='vacancy-form__item scroll-margin-for-fixed-header'
                    ref={getRefById('hoursPerMonthFrom', refMap)?.ref}
                >
                    <p className='vacancy-form__label'>{__t('Режим работы, часов в месяц')}</p>
                    <Range
                        idFrom='hoursPerMonthFrom'
                        idTo='hoursPerMonthTo'
                        fromValue={hoursPerMonthFrom}
                        toValue={hoursPerMonthTo}
                        onChange={inputChangeHandler}
                        minValue='1'
                    />
                    <ErrorMessage errors={errors?.hoursPerMonthFrom} />
                    <ErrorMessage errors={errors?.hoursPerMonthTo} />
                </div>
            </div>
        </div>
    );
};
